'use client'

import React from 'react'
import { Section } from '@/components/Section'
import RichText from '@/components/RichText'
import { GrantProcessBlock } from '@/payload-types'
import { Heading } from '@/components/ui/heading'
import { Eyebrow } from '@/components/Eyebrow'
import { Text } from '@/components/ui/text'
import { Separator } from '@/components/Separator'
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion'
import { Subheading } from '@/components/ui/subheading'
import { Color } from '@/utilities/colors'
import { cn } from '@/utilities/ui'

export function GrantProcessClient({
  heading,
  description,
  committee,
  step1,
  step2,
  step3,
}: GrantProcessBlock) {
  return (
    <Section bgClassName="bg-paper-1">
      <div className="flex flex-col gap-[60px] max-w-readable-lg mr-auto">
        <div className="flex flex-col gap-[40px]">
          <Heading size="h2">{heading}</Heading>
          {description && <RichText data={description} />}
          <div className="flex flex-col gap-[30px] bg-rock-1 p-[30px]">
            <Eyebrow variant="dark" label="Grant Committee" color="slime" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-[30px]">
              <div className="flex-col">
                <Text size="sm" className="font-bold font-diatype">
                  From the Sia Foundation:
                </Text>
                <ul className="list-disc list-inside flex flex-col">
                  {committee?.employees?.map((name) => (
                    <li key={name.id}>
                      <Text size="sm" className="font-normal font-diatype">
                        {name.name}
                      </Text>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="">
                <Text size="sm" className="font-bold font-diatype">
                  Community Members:
                </Text>
                <ul className="list-disc list-inside flex flex-col">
                  {committee?.community?.map((name) => (
                    <li key={name.id}>
                      <Text size="sm" className="font-normal font-diatype">
                        {name.name}
                      </Text>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
        <Separator color="rock-2" />
        <StepSection label="STEP 1" color="punch">
          <RichText className="prose-info prose-dark" data={step1.content} />
          <Accordion type="single" collapsible className="flex flex-col gap-[30px]">
            <AccordionItem
              value="small-grants"
              className="border-2 border-olive-2 px-[20px] py-[20px]"
            >
              <AccordionTrigger>
                <Subheading size="md">Small Grants</Subheading>
              </AccordionTrigger>
              <AccordionContent>
                <RichText
                  className="prose-info prose-dark pt-[20px] text-start"
                  data={step1.smallGrants}
                />
              </AccordionContent>
            </AccordionItem>
            <AccordionItem
              value="medium-grants"
              className="border-2 border-olive-2 px-[20px] py-[20px]"
            >
              <AccordionTrigger>
                <Subheading size="md">Standard Grants</Subheading>
              </AccordionTrigger>
              <AccordionContent>
                <RichText
                  className="prose-info prose-dark pt-[20px] text-start"
                  data={step1.mediumGrants}
                />
              </AccordionContent>
            </AccordionItem>
            <AccordionItem
              value="large-grants"
              className="border-2 border-olive-2 px-[20px] py-[20px]"
            >
              <AccordionTrigger>
                <Subheading size="md">Large Grants</Subheading>
              </AccordionTrigger>
              <AccordionContent>
                <RichText
                  className="prose-info prose-dark pt-[20px] text-start"
                  data={step1.largeGrants}
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </StepSection>
        <Separator color="rock-2" />
        <StepSection label="STEP 2" color="punch">
          <RichText className="prose-info prose-dark" data={step2.content} />
        </StepSection>
        <Separator color="rock-2" />
        <StepSection label="STEP 3" color="punch">
          <RichText className="prose-info prose-dark" data={step3.content} />
        </StepSection>
      </div>
    </Section>
  )
}

const StepSection: React.FC<{
  color: Color
  label: string
  children: React.ReactNode
  className?: string
}> = ({ label, color, children, className }) => {
  return (
    <div className={cn('flex flex-col', className)}>
      <div className="pt-2">
        <Eyebrow color={color} variant="dark" label={label} />
      </div>
      <div className="pl-[40px] flex flex-col gap-2">{children}</div>
    </div>
  )
}
